'use client';

import type { PortableTextBlock } from 'next-sanity';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';
import { convertPortableTextToPlainText } from '../../../sanity/utils/helpers';

const SPLIT_REGEX = /\s+/;
const MIN_WORD_COUNT = 20; // Only truncate if more than 20 words

function countWordsInPortableText(content: PortableTextBlock[]): number {
  const plainText = convertPortableTextToPlainText(content);
  return plainText.trim() ? plainText.trim().split(SPLIT_REGEX).length : 0;
}

export function ProductListingTruncatedDescription({
  content,
  className,
  buttonClassName,
  children,
}: {
  content: PortableTextBlock[] | null;
  className?: string;
  buttonClassName?: string;
  children: (content: PortableTextBlock[]) => ReactNode;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showEllipsis, setShowEllipsis] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      // Check if content exceeds 2 lines by comparing scroll height with client height
      const scrollHeight = contentRef.current.scrollHeight;
      const clientHeight = contentRef.current.clientHeight;
      setShowEllipsis(scrollHeight > clientHeight);
    }
  }, []);

  if (!content) return null;

  const totalWords = countWordsInPortableText(content);
  const hasLongContent = totalWords > MIN_WORD_COUNT;

  if (!hasLongContent) {
    return <div className={className}>{children(content)}</div>;
  }

  return (
    <div className={className}>
      <div className="relative">
        {/* Content with line clamping */}
        <div
          ref={contentRef}
          className={cn(
            'relative transition-all duration-300 ease-out',
            !isExpanded && 'line-clamp-2'
          )}
        >
          {children(content)}
        </div>

        {/* Three dots ellipsis button */}
        {showEllipsis && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className={cn(
              'inline-flex items-center font-medium text-sm transition-colors hover:opacity-70',
              buttonClassName
            )}
            aria-label={isExpanded ? 'Show less' : 'Show more'}
          >
            {isExpanded ? ' Show less' : '…'}
          </button>
        )}
      </div>
    </div>
  );
}
